import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Edit2, Trash2, Trophy, TrendingUp } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AutoplayVariation } from '@/store/videoFeatures/types/autoplay-ab-test';
import { cn } from '@/lib/utils';
import autoPlayPadrao from '@/assets/autoPlayTemplates/autoPlay.svg';
import autoPlayMini from '@/assets/autoPlayTemplates/autoPlayMini.svg';
import autoPlayLarge from '@/assets/autoPlayTemplates/autoPlayLarge.svg';
import autoPlayFullScreen from '@/assets/autoPlayTemplates/autoPlayFullScreen.svg';
import autoPlayTransparent from '@/assets/autoPlayTemplates/autoPlayTransparent.svg';
import autoPlayBlur from '@/assets/autoPlayTemplates/autoPlayBlur.svg';
import autoPlayAnimation from '@/assets/autoPlayTemplates/autoPlayAnimation.svg';

interface VariationCardProps {
  variation: AutoplayVariation;
  isActive?: boolean;
  isPrimary?: boolean;
  trafficPercentage: number;
  onEdit: (variation: AutoplayVariation) => void;
  onDelete: (variationId: string) => void;
  canDelete?: boolean;
}

export const VariationCard: React.FC<VariationCardProps> = ({
  variation,
  isActive = false,
  isPrimary = false,
  trafficPercentage,
  onEdit,
  onDelete,
  canDelete = true
}) => {
  useLanguage();

  return (
    <div
      className={cn(
        "bg-card rounded-xl p-4 group",
        "shadow-[0_2px_12px_rgba(0,0,0,0.04)]",
        "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
        "border-2",
        // Hover shadow apenas para cards NÃO primários
        !isPrimary && "hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)]",
        // Borda diferenciada: Principal = azul sólida (sem glow), Secundária = cinza com hover
        isPrimary
          ? "border-primary"
          : "border-border hover:border-primary/40"
      )}
    >
      {/* Header compacto - apenas badges */}
      <div className="flex items-center gap-2 mb-3">
        {/* Badges de status */}
        <div className="flex items-center gap-1.5 flex-1">
          {!isPrimary && isActive && (
            <Badge className="bg-blue-50 text-blue-600 border-blue-200 gap-1 text-[9px] px-1.5 py-0.5 h-4 shrink-0">
              <span className="relative flex h-1 w-1">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1 w-1 bg-blue-500"></span>
              </span>
              {translateInterfaceText("Em teste")}</Badge>
          )}
          
          {/* Performance Badges */}
          {variation.metrics?.isWinner && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-300 gap-1 text-[9px] px-1.5 py-0.5 h-4 shrink-0 font-medium">
              <Trophy className="h-2.5 w-2.5" />
              {translateInterfaceText("Vencedor")}</Badge>
          )}
          {variation.metrics?.bestCTR && !variation.metrics?.isWinner && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 gap-1 text-[9px] px-1.5 py-0.5 h-4 shrink-0 font-medium">
              <TrendingUp className="h-2.5 w-2.5" />
              {translateInterfaceText("Melhor CTR")}</Badge>
          )}
        </div>
      </div>

      {/* Preview com overlay de ações (padrão Thumbnail) */}
      <div className="relative h-[90px] bg-slate-900 rounded-lg overflow-hidden group/preview">
        {/* Fundo escuro simulando vídeo */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />

        {/* Preview do autoplay centralizado */}
        <div className="absolute inset-0 flex items-center justify-center p-2">
          {variation.templateId === 'custom' && variation.customization.customImage ? (
            // Imagem personalizada
            <img
              src={variation.customization.customImage}
              alt={translateInterfaceText("Overlay personalizado")}
              className="w-full h-full object-contain"
            />
          ) : (
            // Imagem do template
            getTemplateImage(variation.templateId) && (
              <img
                src={getTemplateImage(variation.templateId)!}
                alt={getTemplateName(variation.templateId)}
                className="w-full h-full object-contain"
              />
            )
          )}
        </div>

        {/* Overlay de ações (hover) */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/preview:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-6">
          <button
            type="button"
            className="flex flex-col items-center gap-1 transition-transform hover:scale-110"
            onClick={() => onEdit(variation)}
          >
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-lg">
              <Edit2 className="h-4 w-4 text-primary" />
            </div>
            <span className="text-[10px] text-white font-medium">{translateInterfaceText("Editar")}</span>
          </button>
          
          <button
            type="button"
            className="flex flex-col items-center gap-1 transition-transform hover:scale-110 disabled:opacity-50 disabled:pointer-events-none"
            onClick={() => onDelete(variation.id)}
            disabled={!canDelete}
          >
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-lg">
              <Trash2 className="h-4 w-4 text-destructive" />
            </div>
            <span className="text-[10px] text-white font-medium">{translateInterfaceText("Excluir")}</span>
          </button>
        </div>
      </div>

      {/* Footer: Nome + Tags do teste */}
      <div className="mt-2 pt-2 border-t border-border/50 flex items-center justify-between gap-2">
        {/* Nome (lado esquerdo) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs font-medium text-foreground truncate max-w-[120px] line-clamp-1 cursor-default">
              {variation.name}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[240px]">
            <p className="font-medium">{variation.name}</p>
          </TooltipContent>
        </Tooltip>
        
        {/* Tags (lado direito) */}
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">
            {getTemplateName(variation.templateId)}
          </span>
          {variation.customization.pulseAnimation && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
              {translateInterfaceText("Animação")}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper para mapear templateId para nome legível usando os templates reais
const getTemplateName = (templateId: string): string => {
  const templateNames: Record<string, string> = {
    'autoPlay': 'Padrão',
    'small': 'Pequeno',
    'large': 'Grande',
    'fullScreen': 'Tela Cheia',
    'transparent': 'Transparente',
    'blur': 'Censurado',
    'animation': 'Animado',
    'custom': 'Personalizado'
  };
  return templateNames[templateId] || 'Personalizado';
};

// Helper para mapear templateId para imagem do template
const getTemplateImage = (templateId: string): string | null => {
  const templateImages: Record<string, string> = {
    'autoPlay': autoPlayPadrao,
    'small': autoPlayMini,
    'large': autoPlayLarge,
    'fullScreen': autoPlayFullScreen,
    'transparent': autoPlayTransparent,
    'blur': autoPlayBlur,
    'animation': autoPlayAnimation
  };
  return templateImages[templateId] || null;
};
